import React from "react";
import { ClipboardList, CheckCircle2, AlertCircle, Pencil } from "lucide-react";
import { ProductColumnHeader } from "src/enums";
import { RelabelProductRow, TableColumn } from "src/types";
import { Table } from "src/components";
import { ROUTING_TYPE_CONFIG, RoutingTypeKey } from "src/constants/relabelCases";

interface RelabelProductDetailsTableProps {
  relabelProducts: RelabelProductRow[];
  onEditProductQty?: (productId: string) => void;
}

const RelabelProductDetailsTable: React.FC<RelabelProductDetailsTableProps> = ({
  relabelProducts,
  onEditProductQty
}) => {
  const columns: TableColumn<ProductColumnHeader, RelabelProductRow>[] = [
    {
      header: ProductColumnHeader.TYPE,
      column: (row) => {
        const rtCfg = ROUTING_TYPE_CONFIG[row.routingType as RoutingTypeKey];
        return rtCfg ? (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${rtCfg.badgeBg} ${rtCfg.badgeText}`}>
            {rtCfg.label}
          </span>
        ) : undefined;
      },
      styles: { minWidth: "140px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row) => <div>{row.name}</div>,
      styles: { minWidth: "200px" }
    },
    {
      header: ProductColumnHeader.SIZE,
      column: (row) => <div>{row.size ?? "-"}</div>
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row) => <span className="font-mono text-secondary-600">{row.barcode}</span>
    },
    {
      header: ProductColumnHeader.SKU,
      column: (row) => <span className="font-mono text-secondary-600">{row.skuName}</span>
    },
    {
      header: ProductColumnHeader.EU_RECEIVED,
      column: (row) => <span className="font-semibold text-secondary-900">{row.euReceivedQty}</span>,
      styles: { textAlign: "center" as const }
    },
    {
      header: ProductColumnHeader.STATUS,
      column: (row) => {
        const inProgress = Math.max(0, row.distributedQty - row.existingCasedQty);
        const available = Math.max(0, row.euReceivedQty - row.existingCasedQty);
        const inProgressOver = inProgress > available;
        const existingOver = row.existingCasedQty > row.euReceivedQty;
        const fullyCased = row.existingCasedQty === row.euReceivedQty && inProgress === 0;
        const fullyDistributed = row.distributedQty === row.euReceivedQty && row.distributedQty > 0;

        if (inProgressOver) {
          return (
            <span
              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
              title="In-progress distribution exceeds available qty"
            >
              <AlertCircle className="w-4 h-4" />
              {inProgress - available} over
            </span>
          );
        }
        if (existingOver) {
          return (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"
              title="Already-cased qty exceeds received qty (from prior case edits)"
            >
              <AlertCircle className="w-4 h-4" />
              {row.existingCasedQty - row.euReceivedQty} over (existing)
            </span>
          );
        }
        if (fullyCased || fullyDistributed) return <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />;
        return <span className="text-xs text-secondary-400">{row.euReceivedQty - row.distributedQty} remaining</span>;
      },
      styles: { textAlign: "center" as const }
    }
  ];

  if (onEditProductQty) {
    columns.push({
      header: ProductColumnHeader.PLACE_HOLDER,
      column: (row) => (
        <button
          type="button"
          onClick={() => onEditProductQty(row.id)}
          title="Edit EU quantity with reason"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      ),
      styles: { textAlign: "right" as const, width: "60px" }
    });
  }

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-black px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white/10 p-2">
            <ClipboardList className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white tracking-wide">Relabel Product Details</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {relabelProducts.length} relabel product{relabelProducts.length !== 1 ? "s" : ""} to distribute across
              cases
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        <Table columns={columns} data={relabelProducts} />
      </div>
    </div>
  );
};

export { RelabelProductDetailsTable };
